import { Box } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Panel from 'components/Panel'
import Modal from 'components/Modal'
import Form from 'components/Form'
import { useSnackbar } from 'notistack'
import { setInfo, clearInfo } from 'slices/infoSlice'
import usePopup from 'hooks/usePopup'
import useAxios from 'hooks/useAxios'
import { decentilizedProvinceRes, warpUpFailResponse } from 'utilities/utils'
import { normalizeData } from 'utilities/validator'
import useDataHandler from 'hooks/useDataHandler'

export default function HubMaster() {
	const dispatch = useDispatch()
	const { enqueueSnackbar } = useSnackbar();
	const { save } = useDataHandler()
	const info = useSelector(state => state.info.ctx || {})
	const { post, multiple, del, get } = useAxios();
	const [mode, setMode] = useState("initial")
	const { confirmPopup } = usePopup();
	const [provinceData, setProvinceData] = useState([])
	const [provinceRes, setProvinceRes] = useState([])
	const [allHub, setAllHub] = useState([]);
	const [allRoute, setAllRoute] = useState([])
	const roleUser = useSelector(state => state.user.roleName)

	const fetchData = async controller => {
		const result = await multiple([
			{ method: "get", url: "/hub/getAllHub", config: { signal: controller.signal } },
			{ method: "get", url: "/province/getAllProvince", config: { signal: controller.signal } },
			{ method: "get", url: "/route/getAllActiveRouteUnion", config: { signal: controller.signal } }
		])
		if (result[0].status === 200) {
			const data = result[0].data.data || []
			setAllHub(data)
		}
		if (result[1].status === 200) {
			const data = result[1].data.data
			setProvinceRes(data.records)
			const groupedData = decentilizedProvinceRes(data.records)
			setProvinceData(groupedData)
		}
		if (result[2].status === 200) {
			const data = result[2].data.data.records || []
			setAllRoute(data.map(e => ({ label: e.name, value: e.name })))
		}
	}

	useEffect(() => {
		const controller = new AbortController();
		fetchData(controller)
		return () => {
			controller.abort()
		}
	}, [])

	useEffect(() => {
		if (info.provinceId && provinceRes) {
			const { province, amphoe, district, zipcode } = provinceRes.find(e => info.provinceId === e.id)
			dispatch(setInfo({ province, amphoe, district, zipcode }))
		} else {
			dispatch(setInfo({ province: null, amphoe: null, district: null, zipcode: null }))
		}

	}, [info.provinceId])

	const allProvince = useMemo(() => Object.keys(provinceData).map(child => { return { label: child, value: child } }), [provinceData])
	const allAmphoe = useMemo(() => info.province ? Object.keys(provinceData[info.province]).map(child => { return { label: child, value: child } }) : [], [provinceData, info.province])
	const allDistrict = useMemo(() => provinceData.hasOwnProperty(info.province) && provinceData[info.province].hasOwnProperty(info.amphoe) ? Object.keys(provinceData[info.province][info.amphoe]).map(child => { return { label: child, value: child } }) : [], [provinceData, info.province, info.amphoe])

	const onCloseModal = useCallback(() => {
		confirmPopup({
			title: "แจ้งเตือน",
			content: "ท่านต้องการยกเลิกการแก้ไขหรือไม่",
			onSubmit: () => {
				setMode("initial")
			}
		})
	}, [])

	const inputForm = [
		{
			rowData: [
				{
					type: "text",
					required: true,
					props: {
						id: "name",
						label: "ชื่อ",
						variant: "outlined"
					}
				}, {
					type: "text",
					validation: 'tel',
					props: {
						id: "tel",
						label: "เบอร์โทร",
						variant: "outlined"
					}
				}
			]
		}, {
			rowData: [
				{
					type: "text",
					required: true,
					props: {
						id: "address",
						label: "ที่อยู่",
						variant: "outlined",
						multiline: true,
						maxRows: 4
					}, md: 9
				}, {
					type: "autocomplete",
					required: true,
					props: {
						id: "routeName",
						label: "ต้นทาง",
						options: allRoute
					}, md: 3
				}
			]
		}, {
			rowData: [
				{
					type: "autocomplete",
					required: true,
					props: {
						id: "province",
						label: "จังหวัด",
						options: allProvince
					}
				},
				{
					type: "autocomplete",
					required: true,
					props: {
						id: "amphoe",
						label: "อำเภอ",
						options: allAmphoe,
						relateId: "province"
					}
				},
				{
					type: "autocomplete",
					required: true,
					props: {
						id: "district",
						label: "ตำบล",
						options: allDistrict,
						onChange: (value) => {
							if (value) {
								const zipcode = provinceData[info.province][info.amphoe][value].zipcode
								dispatch(setInfo({ zipcode }))
							} else {
								dispatch(setInfo({ zipcode: null }))
							}
						},
						relateId: "amphoe"
					}
				}, {
					type: "text",
					props: {
						id: "zipcode",
						label: "รหัสไปรษณีย์",
						variant: "outlined",
						readOnly: true,
					}
				}
			]
		}, {
			rowData: [
				mode === "edit" && {
					type: "dropdown",
					props: {
						id: "active",
						label: "สถานะ",
						options: [
							{ label: "ใช้งาน", value: true },
							{ label: "ไม่ใช้งาน", value: false }
						],
					}
				}, {}, {}, {}
			]
		}, {
			rowData: [
				{
					type: "button",
					props: {
						label: "บันทึก",
						onClick: () => {
							saveData()
						},
						className: "!bg-bpTheme-buttonSubmit"
					}
				}
			]
		}
	]

	const table = useMemo(() => [
		{
			rowData: [{
				type: "dataTable",
				props: {
					columns: [
						{
							id: 'no',
							label: 'เลขที่ศูนย์กระจายสินค้า',
							fields: ["no"]
						},
						{
							id: 'name',
							label: 'ชื่อ',
							fields: ["name"]
						},
						{
							id: 'address',
							label: 'ที่อยู่',
							fields: ["address"]
						},
						{
							id: 'routeName',
							label: 'ต้นทาง',
							fields: ["routeName"]
						},
						{
							id: 'tel',
							label: 'เบอร์โทร',
							fields: ["tel"]
						},
						{
							id: 'active',
							label: 'สถานะ',
							fields: ["active"],
							valuesMap: [{ label: "ใช้งาน", value: true }, { label: "ไม่ใช้งาน", value: false }]

						}
					],
					rows: allHub,
					size: "small",
					showDeleteButton: roleUser === "Owner",
					title: "ข้อมูลศูนย์กระจายสินค้า",
					showSystemFields: true,
					onClick: (e) => {
						setMode("edit")
						dispatch(setInfo(e))
					},
					onDelete: (e, onSuccess) => {
						confirmPopup({
							onSubmit: async () => {
								const result = await del("/hub/deleteHub", { ids: e.map(data => data.id).join(',') })
								if (result.status === 200) {
									enqueueSnackbar('ลบสำเร็จ', { variant: "success" })
									dispatch(clearInfo())
									await fetchData(new AbortController())
									onSuccess()
								} else {
									enqueueSnackbar('ลบไม่สำเร็จ', { variant: "error" })
								}
							}
						})
					},
					controller: (paging) => get("/hub/getAllHub", { paging }),
					searchFields: [
						{ label: "ชื่อ", value: "name" },
						{ label: "ที่อยู่", value: "address" },
						{ label: "เบอร์โทร", value: "tel" },
						{ label: "สถานะ", value: "active" }
					]
				}
			}]
		}, {
			noCol: true,
			rowData: [
				{
					type: "button",
					props: {
						label: "เพิ่มใหม่",
						onClick: () => {
							setMode("create")
							dispatch(clearInfo())
						}
					},
					permissions: ["write", "create"]
				}
			]
		}
	], [allHub])

	const saveData = useCallback(() => {
		save({
			data: info,
			onSubmit: async () => {
				const provinceId = info.district ? provinceData[info.province][info.amphoe][info.district].id : null
				const data = normalizeData(info)
				let response
				if (mode === "create") {
					response = await post("/hub/createHub", { ...data, provinceId, active: true })
				} else if (mode === "edit") {
					response = await post("/hub/updateHub", { ...data, provinceId })
				}
				if (response.status === 200) {
					setMode("initial")
					enqueueSnackbar('บันทึกสำเร็จ', { variant: "success" })
					fetchData(new AbortController())
				} else {
					enqueueSnackbar(warpUpFailResponse(response, 'บันทึกไม่สำเร็จ'), { variant: "error" })
				}
			},
			onCancel: () => {
				enqueueSnackbar('ยกเลิกรายการ', { variant: "info" })
			}
		})
	}, [info, mode])

	return (
		<Box>
			<Panel data={table}></Panel>
			<Modal open={mode === "create" || mode === "edit"} onClose={onCloseModal} className="form-modal">
				<Form title={"ศูนย์กระจายสินค้า " + (info.no || "")} name="user-form" data={inputForm}></Form>
			</Modal>
		</Box>
	)
}
